import { v4 as uuidv4 } from "uuid";
import { AxiosResponse } from "axios";
import { createAction, createAsyncThunk } from "@reduxjs/toolkit";
import { axiosClient, axiosLogin } from "../../helpers/apiClient";
import {
  signIn as signInUrl,
  refreshToken as refreshTokenUrl,
  getProducts as getProductsUrl,
  getAllWalletBalance as getAllWalletBalanceUrl,
  getProfileDetail as getProfileDetailUrl,
  getBankInformation as getBankInformationUrl,
  sponsorGenealogy as sponsorGenealogyUrl,
  getPlacementGenealogy as getPlacementGenealogyUrl,
  getPlacementGenealogyInfo as getPlacementGenealogyInfoUrl,
  getMemberById as getMemberByIdUrl,
  encryptPassword as encryptPasswordUrl,
  getMemberByUsername as getMemberByUsernameUrl,
  getBankList as getBankListUrl,
  updateBankInfo as updateBankInfoUrl,
  updateProfileInfo as updateProfileInfoUrl,
  getAddressByMemberId as getAddressByMemberIdUrl,
  getAddressById as getAddressByIdUrl,
  addAddress as addAddressUrl,
  updateAddress as updateAddressUrl,
  deleteAddress as deleteAddressUrl,
  changePassword as changePasswordUrl,
  changeSecurityPassword as changeSecurityPasswordUrl,
  registerMember as registerMemberUrl,
  forgetPassword as forgetPasswordUrl,
  verifyToken as verifyTokenUrl,
  resetPassword as resetPasswordUrl,
} from "../../helpers/apiUrl";
import { setCookie, getCookie, eraseCookie } from "../../helpers/cookies";
import {
  bankInformationResponse,
  profileDetailResponse,
  SignInPayload,
  SignInResponse,
  SponsorGenealogy,
  Member,
  PlacementGenealogyInfo,
  PlacementGenealogy,
  payloadBankInfo,
  PlacementMaster,
  PlacementChild,
  PlcementNodeType,
  EmptyNodeType,
  payloadUpdateProfile,
  Address,
  addAddressPayload,
  updateAddressPayload,
  deleteAddressPayload,
  payloadChangePassword,
  payloadChangeSecurityPassword,
  RegisterMemberPayload,
  ForgetPasswordPayload,
  ResetPasswordPayload,
} from "../../types/user";
import { Response } from "../../types/global";
import { Node, Edge } from "reactflow";

export const refreshToken = createAsyncThunk("user/refresh", async () => {
  const authToken = getCookie("authToken");
  if (!authToken) {
    return {
      isAuth: false,
    };
  }

  const authResponse = await axiosLogin
    .post(refreshTokenUrl, { token: authToken })
    .then((d: AxiosResponse<Response<SignInResponse>>) => d.data)
    .then((d: Response<SignInResponse>) => d.data);

  return {
    isAuth: true,
    profile: authResponse,
  };
});

export const register = createAsyncThunk("user/register", async () => {
  return;
});

export const login = createAsyncThunk(
  "user/login",
  async (profile: SignInPayload) => {
    const authResponse = await axiosLogin
      .post(signInUrl, profile)
      .then((d: AxiosResponse<Response<SignInResponse>>) => d.data)
      .then((d: Response<SignInResponse>) => d.data)
      .catch((err) => {
        throw err;
      });

    if (authResponse && authResponse.token) {
      setCookie("authToken", authResponse.token!, 1);
      // setCookie("refreshToken", authResponse.refreshToken, 1)
      // setCookie("email", authResponse.email, 1)
      // delete authResponse.authToken
      // delete authResponse.refreshToken
    }

    return authResponse;
  },
);

export const logout = createAsyncThunk("user/logout", async () => {
  eraseCookie("refreshToken");
  return;
});

export const forgetPassword = async (payload: ForgetPasswordPayload) => {
  return await axiosLogin
    .post(forgetPasswordUrl, payload)
    .then((d: AxiosResponse<Response<string>>) => d.data)
    .then((d: Response<string>) => d.data);
};

export const verifyToken = async (token: string) => {
  return await axiosLogin
    .get(`${verifyTokenUrl}?accessToken=${token}`)
    .then((d: AxiosResponse<Response<number>>) => d.data);
};

export const resetPassword = async (payload: ResetPasswordPayload) => {
  return await axiosLogin
    .post(resetPasswordUrl, payload)
    .then((d: AxiosResponse<Response<null>>) => d.data);
};

export const getProfileDetail = createAsyncThunk(
  "member/get-member-by-id",
  async (memberId: number) => {
    const urlWithMemberId = `${getProfileDetailUrl}/?id=${memberId}`;
    return await axiosClient
      .get(urlWithMemberId)
      .then((d: AxiosResponse<Response<profileDetailResponse>>) => d.data)
      .then((d: Response<profileDetailResponse>) => d.data);
  },
);

export const getSponsorGenealogy = createAsyncThunk(
  "member/sponsor-genealogy",
  async (memberId: number) => {
    const payload = {
      memberId,
    };
    const genealogy = await axiosClient
      .post(sponsorGenealogyUrl, payload)
      .then((d: AxiosResponse<Response<SponsorGenealogy[]>>) => d.data)
      .then((d: Response<SponsorGenealogy[]>) => d.data);

    //grouping
    let tempStore: Record<number, SponsorGenealogy[]> = {};
    let maxLevel = 0;
    for (let i = 0; i < genealogy.length; i++) {
      const member = genealogy[i];
      if (member.levelx == 0) {
        continue;
      }

      // group children
      if (member.levelx in tempStore) {
        tempStore[member.levelx].push(member);
      } else {
        tempStore[member.levelx] = [member];
      }

      // get level depth
      if (member.levelx > maxLevel) {
        maxLevel = member.levelx;
      }
    }

    let formated: SponsorGenealogy[] = [];
    for (let i = 0; i < maxLevel; i++) {
      const childLevel = maxLevel - i;
      const parentLevel = childLevel - 1;
      if (childLevel == 1) {
        formated = tempStore[childLevel] as any;
        break;
      }

      for (let j = 0; j < tempStore[childLevel].length; j++) {
        const child = tempStore[childLevel][j];
        if (!(parentLevel in tempStore)) {
          continue;
        }

        const parentIndex = tempStore[parentLevel].findIndex(
          (parent) => parent.id == child.sponsorId,
        );
        if (parentIndex == -1) {
          continue;
        }

        //push to parent
        if (tempStore[parentLevel][parentIndex].child) {
          tempStore[parentLevel][parentIndex].child!.push(child);
        } else {
          tempStore[parentLevel][parentIndex].child! = [child];
        }
      }
    }

    return formated;
  },
);

export const GetMemberById = async (memberId: number) => {
  const membership = await axiosClient
    .get(`${getMemberByIdUrl}?id=${memberId}`)
    .then((d: AxiosResponse<Response<Member>>) => d.data)
    .then((d: Response<Member>) => d.data);

  return membership;
};

type getPlacementGenealogyPayload = {
  memberId: number;
  dlMemberId: number;
  userName: string;
};

export const getPlacementGenealogy = createAsyncThunk(
  "user/getPlacementGenealogy",
  async ({ memberId, dlMemberId, userName }: getPlacementGenealogyPayload) => {
    //get list node
    const payload = {
      memberId,
      level: 3,
      dlMemberId,
      dlUsername: userName,
    };

    const genealogyInfo = await axiosClient
      .post(getPlacementGenealogyUrl, payload)
      .then((d: AxiosResponse<Response<PlacementGenealogy[]>>) => d.data)
      .then((d: Response<PlacementGenealogy[]>) => d.data);

    //grouping
    let tempStore: Record<number, PlacementGenealogy[]> = {};
    let maxLevel = 0;
    let minLevel = genealogyInfo[0].level;
    for (let i = 0; i < genealogyInfo.length; i++) {
      const member = genealogyInfo[i];
      // if (memberId == dlMemberId && member.level == 0) {
      //   continue;
      // }

      // group children
      if (member.level in tempStore) {
        tempStore[member.level].push(member);
      } else {
        tempStore[member.level] = [member];
      }

      // get level depth
      if (member.level > maxLevel) {
        maxLevel = member.level;
      }
      //get top level
      if (member.level < minLevel) {
        minLevel = member.level;
      }
    }

    let formated: PlacementChild[] = [];
    for (let i = 0; i <= maxLevel; i++) {
      const childLevel = maxLevel - i;
      const parentLevel = childLevel - 1;
      if (childLevel == 0) {
        tempStore[childLevel].forEach((child: any) => {
          child.type = "child";
        });
        formated = tempStore[childLevel] as any;
        break;
      }

      for (let j = 0; j < tempStore[childLevel].length; j++) {
        const child = tempStore[childLevel][j];
        if (!(parentLevel in tempStore)) {
          continue;
        }

        const parentIndex = tempStore[parentLevel].findIndex(
          (parent) => parent.memberId == child.placementId,
        );
        if (parentIndex == -1) {
          continue;
        }

        //push to parent
        if (tempStore[parentLevel][parentIndex].child) {
          tempStore[parentLevel][parentIndex].child!.push({
            ...child,
            type: "child",
          } as PlacementChild);
        } else {
          tempStore[parentLevel][parentIndex].child! = [
            {
              ...child,
              type: "child",
            } as PlacementChild,
          ];
        }
      }
    }

    // let binaryTree: PlacementMaster | PlacementChild;
    // if (memberId === dlMemberId) {
    //   if (userName === "") {
    //     //get top node
    //     const topGenealogy = await axiosClient
    //       .get(`${getPlacementGenealogyInfoUrl}?memberId=${memberId}`)
    //       .then((d: AxiosResponse<Response<PlacementGenealogyInfo>>) => d.data)
    //       .then((d: Response<PlacementGenealogyInfo>) => d.data);

    //     binaryTree = {
    //       ...topGenealogy,
    //       child: formated,
    //       type: "master",
    //     } as PlacementMaster;
    //   } else {
    //     const urlWithUsername = `${getMemberByUsernameUrl}/?username=${userName}`;
    //     const userProfile = await axiosClient
    //       .get(urlWithUsername)
    //       .then((d: AxiosResponse) => d.data)
    //       .then((d: AxiosResponse) => d.data);

    //     const topGenealogy = await axiosClient
    //       .get(`${getPlacementGenealogyInfoUrl}?memberId=${userProfile.id}`)
    //       .then((d: AxiosResponse<Response<PlacementGenealogyInfo>>) => d.data)
    //       .then((d: Response<PlacementGenealogyInfo>) => d.data);

    //     binaryTree = {
    //       ...topGenealogy,
    //       child: formated,
    //       type: "master",
    //     } as PlacementMaster;
    //   }
    // } else {
    //   const memberProfile = await GetMemberById(dlMemberId);

    //   const topGenealogy = await axiosClient
    //     .get(`${getPlacementGenealogyInfoUrl}?memberId=${memberProfile.id}`)
    //     .then((d: AxiosResponse<Response<PlacementGenealogyInfo>>) => d.data)
    //     .then((d: Response<PlacementGenealogyInfo>) => d.data);

    //   // binaryTree = formated[0]
    //   binaryTree = {
    //     ...topGenealogy,
    //     child: formated,
    //     type: "master",
    //   } as PlacementMaster;
    // }

    let nodes: (
      | Node<PlcementNodeType, "downline">
      | Node<EmptyNodeType, "empty">
    )[] = [];
    let edges: Edge[] = [];
    const blockHorizontalHap = 150;
    const blockVerticalHap = 350;
    const maxLevelScope = 3; // start from zero

    const nodeConversion = (
      node: PlacementChild,
      level: number,
      xposition: number,
      sourceId?: string,
    ) => {
      const { child, ...remainingFields } = node;
      const nodeId = uuidv4();

      let x = 0;
      if (level == 0) {
        x = 0;
      } else if (remainingFields.placementPosition == "L") {
        x = xposition - blockHorizontalHap - (maxLevelScope - level) * 150;
      } else {
        x = xposition + blockHorizontalHap + (maxLevelScope - level) * 150;
      }

      nodes.push({
        id: nodeId,
        type: "downline",
        position: {
          x: x,
          y: level * blockVerticalHap,
        },
        data: remainingFields,
        draggable: false,
      });

      if (remainingFields.type == "child" && sourceId) {
        edges.push({
          id: uuidv4(),
          type: "step",
          source: sourceId,
          sourceHandle: "bottom",
          target: nodeId,
          targetHandle: "top",
        });
      }

      if (child && child.length !== 0) {
        let left = false;
        let right = false;
        for (let i = 0; i < child.length; i++) {
          const downline = child[i];
          if (!left) {
            left = downline.placementPosition == "L";
          }

          if (!right) {
            right = downline.placementPosition == "R";
          }

          nodeConversion(downline, level + 1, x, nodeId);
        }

        if (level < maxLevelScope) {
          if (!left) {
            addLeftEmptyNode(x, level, nodeId, true, remainingFields);
          }

          if (!right) {
            addRightEmptyNode(x, level, nodeId, true, remainingFields);
          }
        }
      } else if (level < maxLevelScope) {
        //if dont have children
        addLeftEmptyNode(
          x + (level * 100 - 50),
          level,
          nodeId,
          true,
          remainingFields,
        );
        // 2, 100
        // 0, -50
        addRightEmptyNode(x + 50, level, nodeId, true, remainingFields);
      }
    };

    const addLeftEmptyNode = (
      x: number,
      level: number,
      nodeId: string,
      canLink: boolean,
      parentDetails?: Omit<PlacementChild, "child">,
    ) => {
      const emptyNodeId = uuidv4();
      const xpost = x - blockHorizontalHap;
      nodes.push({
        id: emptyNodeId,
        type: "empty",
        position: {
          x: xpost,
          y: (level + 1) * blockVerticalHap,
        },
        data: {
          canLink,
          parentDetails,
          isBottom: !(level + 1 < maxLevelScope),
        },
        draggable: false,
      });

      edges.push({
        id: uuidv4(),
        type: "step",
        source: nodeId,
        sourceHandle: "bottom",
        target: emptyNodeId,
        targetHandle: "top",
      });

      if (level + 1 < maxLevelScope) {
        const nextLevel = level + 1;
        addLeftEmptyNode(xpost + nextLevel * 50, nextLevel, emptyNodeId, false);

        addRightEmptyNode(
          xpost - nextLevel * 50,
          nextLevel,
          emptyNodeId,
          false,
        );
      }
    };

    const addRightEmptyNode = (
      x: number,
      level: number,
      nodeId: string,
      canLink: boolean,
      parentDetails?: Omit<PlacementChild, "child">,
    ) => {
      const emptyNodeId = uuidv4();
      const xpost = x + blockHorizontalHap;
      nodes.push({
        id: emptyNodeId,
        type: "empty",
        position: {
          x: xpost,
          y: (level + 1) * blockVerticalHap,
        },
        data: {
          canLink,
          parentDetails,
          isBottom: !(level + 1 < maxLevelScope),
        },
        draggable: false,
      });

      edges.push({
        id: uuidv4(),
        type: "step",
        source: nodeId,
        sourceHandle: "bottom",
        target: emptyNodeId,
        targetHandle: "top",
      });

      if (level + 1 < maxLevelScope) {
        const nextLevel = level + 1;
        addLeftEmptyNode(xpost + nextLevel * 50, nextLevel, emptyNodeId, false);

        addRightEmptyNode(
          xpost - nextLevel * 50,
          nextLevel,
          emptyNodeId,
          false,
        );
      }
    };

    nodeConversion(formated[0], 0, 0);

    return {
      nodes,
      edges,
    };
  },
);

export const hoverPlacementNode = createAction(
  "user/placement/hover",
  (payload: { nodeId: string; edges: Edge[] }) => {
    let selectIds: string[] = [payload.nodeId];

    const getlinkedIds = (nodeId: string, eds: Edge[], self: string[]) => {
      eds
        .filter((edge) => edge.source == nodeId)
        .map((edge) => edge.target)
        .forEach((edgeId) => {
          getlinkedIds(edgeId, eds, self);
          self.push(edgeId);
        });
    };

    getlinkedIds(payload.nodeId, payload.edges, selectIds);

    return {
      payload: selectIds,
    };
  },
);

export const clearPlacementNodeHover = createAction(
  "user/placement/hover/clear",
  () => {
    return {
      payload: "",
    };
  },
);

export const getBankInformation = createAsyncThunk(
  "member/get-member-bank-by-memberid",
  async (memberId: number) => {
    const urlWithMemberId = `${getBankInformationUrl}/?memberid=${memberId}`;
    return await axiosClient
      .get(urlWithMemberId)
      .then((d: AxiosResponse<Response<bankInformationResponse[]>>) => d.data)
      .then((d: Response<bankInformationResponse[]>) => d.data);
  },
);

export const getIdByMemberId = createAsyncThunk(
  "member/get-member-by-username",
  async (username: string) => {
    const urlWithUsername = `${getMemberByUsernameUrl}/?username=${username}`;
    return axiosClient
      .get(urlWithUsername)
      .then((d: AxiosResponse) => d.data)
      .then((d: AxiosResponse) => d.data);
  },
);

export const updateBankInfo = createAsyncThunk(
  "member/update-member-bank",
  async (payload: payloadBankInfo) => {
    return axiosClient
      .post(updateBankInfoUrl, payload)
      .then((d: AxiosResponse) => d.data)
      .then((d: AxiosResponse) => d.data);
  },
);

export const updateProfileInfo = createAsyncThunk(
  "member/update-member",
  async (payload: payloadUpdateProfile) => {
    return axiosClient
      .post(updateProfileInfoUrl, payload)
      .then((d: AxiosResponse) => d.data)
      .then((d: AxiosResponse) => d.data);
  },
);

export const getAddressByMemberId = createAsyncThunk(
  "member/address",
  async (memberId: number) => {
    return await axiosClient
      .get(`${getAddressByMemberIdUrl}?memberid=${memberId}`)
      .then((d: AxiosResponse<Response<Address[]>>) => d.data)
      .then((d: Response<Address[]>) => d.data);
  },
);

export const getAddressById = createAsyncThunk(
  "address",
  async (addressId: number) => {
    return await axiosClient
      .get(`${getAddressByIdUrl}?id=${addressId}`)
      .then((d: AxiosResponse<Response<Address>>) => d.data)
      .then((d: Response<Address>) => d.data);
  },
);

export const addAddress = createAsyncThunk(
  "member/address/add",
  async (payload: addAddressPayload) => {
    // type response = {
    //   count: number
    //   data: string
    //   errorCode: null
    //   msg: string| null
    //   status: boolean
    // }

    await axiosClient.post(addAddressUrl, payload);
    // .then((d: AxiosResponse<Response<Address[]>>) => d.data)
    // .then((d: Response<Address[]>) => d.data);
    return true;
  },
);

export const updateAddress = createAsyncThunk(
  "member/address/update",
  async (payload: updateAddressPayload) => {
    // type response = {
    //   count: number
    //   data: string
    //   errorCode: null
    //   msg: string| null
    //   status: boolean
    // }

    await axiosClient.post(updateAddressUrl, payload);
    // .then((d: AxiosResponse<Response<Address[]>>) => d.data)
    // .then((d: Response<Address[]>) => d.data);
    return true;
  },
);

export const deleteAddress = createAsyncThunk(
  "member/address/delete",
  async (payload: deleteAddressPayload) => {
    // type response = {
    //   count: number
    //   data: string
    //   errorCode: null
    //   msg: string| null
    //   status: boolean
    // }

    await axiosClient.post(deleteAddressUrl, payload);
    // .then((d: AxiosResponse<Response<Address[]>>) => d.data)
    // .then((d: Response<Address[]>) => d.data);
    return true;
  },
);

export const changePassword = createAsyncThunk(
  "member/update-own-password",
  async (payload: payloadChangePassword) => {
    return axiosClient
      .post(changePasswordUrl, payload)
      .then((d: AxiosResponse) => d.data)
      .then((d: AxiosResponse) => d);
  },
);

export const changeSecurityPassword = createAsyncThunk(
  "member/update-own-security-password",
  async (payload: payloadChangeSecurityPassword) => {
    return axiosClient
      .post(changeSecurityPasswordUrl, payload)
      .then((d: AxiosResponse) => d.data)
      .then((d: AxiosResponse) => d);
  },
);

export const registerMember = createAsyncThunk(
  "member/register",
  async (payload: RegisterMemberPayload) => {
    type response = {
      count: number;
      data: string;
      errorCode: null;
      msg: string | null;
      status: boolean;
    };

    return axiosClient
      .post(registerMemberUrl, payload)
      .then((d: AxiosResponse<response>) => d.data);
  },
);
